import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import DefaultLayout from 'app/layouts/DefaultLayout';
import MainContentLayout from 'app/layouts/MainContentLayout';
import Path from 'app/route/Path';
import { toast } from 'react-toastify';
import GA, { EVENT_NAMES } from 'app/services/googleAnalytics';
import BasicInfoSection from '../CreateMenuPage/components/BasicInfoSection';
import DetailedSettingsSection from '../CreateMenuPage/components/DetailedSettingsSection';
import CombinationSection from '../CreateMenuPage/components/CombinationSection';
import NotesSection from '../CreateMenuPage/components/NotesSection';
import PublicSettingsSection from '../CreateMenuPage/components/PublicSettingsSection';
import LoadingProgress from 'app/components/commonUI/LoadingProgress';
import { getMenuById, updateMenu } from 'app/services/api';
import NavigationBackBtn from '../../../components/common/NavigationBackButton';

function EditMenuManagementPage() {
  const navigate = useNavigate();
  const { menuId } = useParams();
  const [customTags, setCustomTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const menuData = {
        name: values.name,
        description: values.description,
        category: values.category,
        treatmentTime: parseInt(values.treatmentTime, 10),
        managementTags: values.managementTags,
        targetAudiences: values.targetAudiences,
        recommendedMenus: values.recommendedMenus,
        avoidSimultaneousMenus: values.avoidSimultaneousMenus,
        avoidConditions: values.avoidConditions,
        customerNotes: values.customerNotes,
        publicStatus: values.publicStatus,
        showOnBookingSite: values.showOnBookingSite,
        isDraft: false,
      };

      const res = await updateMenu(menuId, menuData);
      if (res.success) {
        toast.success('メニューを更新しました');
        GA.tracking(EVENT_NAMES.MENU_LIST?.CLICK_UPDATE, { menuId });
        navigate(Path.menuManagement);
      } else {
        toast.error('メニューの更新に失敗しました');
      }
    } catch (error) {
      console.error('Update menu error:', error);
      toast.error('メニューの更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('メニュー名は必須です'),
    description: Yup.string().trim().required('説明文は必須です'),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      category: '',
      treatmentTime: '',
      managementTags: [],
      targetAudiences: [],
      recommendedMenus: [],
      avoidSimultaneousMenus: [],
      avoidConditions: [],
      customerNotes: '',
      publicStatus: true,
      showOnBookingSite: true,
      isDraft: false,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit,
  });

  const { values, errors, setFieldValue, handleSubmit, setValues, dirty } = formik;

  useEffect(() => {
    const loadMenuData = async () => {
      setIsLoading(true);
      try {
        const res = await getMenuById(menuId);
        const menu = res.data;

        if (menu) {
          setValues({
            name: menu.name || '',
            description: menu.description || '',
            category: menu.category || '',
            treatmentTime: menu?.treatmentTime ? menu?.treatmentTime?.toString() : '',
            managementTags: menu.managementTags || [],
            targetAudiences: menu.targetAudiences || [],
            recommendedMenus: menu.recommendedMenus || [],
            avoidSimultaneousMenus: menu.avoidSimultaneousMenus || [],
            avoidConditions: menu.avoidConditions || [],
            customerNotes: menu.customerNotes || '',
            publicStatus: menu.publicStatus || false,
            showOnBookingSite: menu.showOnBookingSite !== false,
            isDraft: menu.isDraft || false,
          });

          const defaultTags = ['新規顧客限定', '平日限定', '期間限定'];
          const custom = (menu.managementTags || []).filter((tag) => !defaultTags.includes(tag));
          setCustomTags(custom);
        } else {
          toast.error('メニューが見つかりません');
          navigate(Path.menuManagement);
        }
      } catch (error) {
        console.error('Load menu error:', error);
        toast.error('メニュー取得に失敗しました');
        navigate(Path.menuManagement);
      } finally {
        setIsLoading(false);
      }
    };

    loadMenuData();
  }, [menuId]);

  if (isLoading) {
    return (
      <DefaultLayout>
        <MainContentLayout>
          <LoadingProgress />
        </MainContentLayout>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <MainContentLayout containerClass="!overflow-visible">
        <div className="w-full mx-auto">
          <div className="sticky top-[38px] z-40 bg-white/95 backdrop-blur-sm flex items-center justify-between px-4 md:px-0 border-b border-slate-100 h-14 -mt-4">
            <h2 className="text-base lg:text-xl font-semibold">
              <NavigationBackBtn url={Path.menuManagement} text="メニュー編集" />
            </h2>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={!dirty}
                className={`bg-blue-btn-primary text-white text-xs md:text-sm font-bold px-4 py-2 rounded-lg shadow-sm ${
                  dirty ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
                }`}
              >
                保存する
              </button>
            </div>
          </div>

          <div className="w-full">
            <BasicInfoSection values={values} errors={errors} setFieldValue={setFieldValue} />

            <DetailedSettingsSection
              values={values}
              setFieldValue={setFieldValue}
              customTags={customTags}
              setCustomTags={setCustomTags}
            />

            <CombinationSection values={values} setFieldValue={setFieldValue} />

            <NotesSection values={values} setFieldValue={setFieldValue} />

            <PublicSettingsSection values={values} setFieldValue={setFieldValue} />
          </div>
        </div>
      </MainContentLayout>
    </DefaultLayout>
  );
}

export default EditMenuManagementPage;
